import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { InputField } from '../../../components/ui/input-field';
import { Button } from '../../../components/ui/button';
import { supabase } from '../../../integrations/supabase/client';

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

interface LoginFormData {
    email: string;
    password: string;
}

const Login: React.FC = () => {
    const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const { login } = useAuth();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        setError(null);
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/dashboard`,
                },
            });
            if (error) throw error;
        } catch (error: any) {
            console.error('Error con Google:', error);
            setError(error.message || 'Error al iniciar sesión con Google');
            setGoogleLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { error } = await login(formData.email, formData.password);
            if (error) {
                setError(error.message || 'Error al iniciar sesión');
            } else {
                window.location.href = '/dashboard';
            }
        } catch (error: any) {
            setError(error.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = () => {
        window.location.href = '/register';
    };

    const handleForgotPassword = () => {
        window.location.href = '/password-recovery';
    };

    return (
        <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
            <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                    alt="Maxi Chatbot Whatsapp AI"
                    className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                />

                <div className="w-[325px] flex flex-col max-md:w-full max-md:max-w-[400px] max-sm:w-full max-sm:max-w-xs">
                    <h1 className="text-[#2B3A51] text-center text-lg font-bold mb-[30px] max-sm:text-base max-sm:mb-5">
                        Inicio de sesión
                    </h1>

                    {error && <div className="mb-4 text-red-500 text-center text-sm">{error}</div>}

                    {/* Google Sign In */}
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={googleLoading}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-[45px] bg-white hover:bg-gray-50 transition-colors mb-4 disabled:opacity-50"
                    >
                        <GoogleIcon />
                        <span className="text-sm font-medium text-gray-700">
                            {googleLoading ? 'Conectando...' : 'Continuar con Google'}
                        </span>
                    </button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-gray-300" />
                        <span className="text-xs text-gray-500">o con correo</span>
                        <div className="flex-1 h-px bg-gray-300" />
                    </div>

                    <form onSubmit={handleLogin} className="w-full">
                        <InputField
                            label="Correo Electrónico"
                            placeholder="Correo electrónico"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                        <InputField
                            label="Contraseña"
                            placeholder="Contraseña"
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                        />
                        <Button type="submit" variant="default" size="lg" className="w-full mb-3 rounded-[45px] mt-4" disabled={loading}>
                            {loading ? 'Ingresando...' : 'Ingresar'}
                        </Button>
                    </form>
                    <Button variant="secondary" onClick={handleRegister} size="lg" className="mb-3 rounded-[45px]">
                        Registrarse
                    </Button>
                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="text-[#2B3A51] text-center text-sm font-normal max-sm:text-[13px] bg-transparent border-none cursor-pointer hover:underline"
                    >
                        ¿Olvidaste tu contraseña?
                    </button>
                </div>
            </div>
            <section
                className="w-6/12 h-full relative overflow-hidden max-md:w-full max-md:h-[400px] max-sm:h-[300px] max-md:hidden"
                aria-label="Background image section"
            >
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                    alt="Investment types in Colombia background"
                    className="w-full h-full object-cover"
                />
            </section>
        </main>
    );
};

export default Login;
